import { useEffect, useState } from 'react';
import { Plus, Search, Building2, MapPin, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { branchAPI, userAPI } from '@/lib/api';
import { toast } from 'sonner';

const BranchCard = ({ branch, childCount }) => {
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`branch-card-${branch.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Building2 className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">{branch.name}</h3>
              <p className="text-sm font-mono text-slate-500">{branch.code}</p>
            </div>
          </div>
          <Badge variant={branch.is_active ? "default" : "secondary"}>
            {branch.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        {branch.address && (
          <p className="text-sm text-slate-600 mt-3 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {branch.address}, {branch.city}, {branch.state}
          </p>
        )}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-sm text-slate-500">
          <span>{branch.timezone}</span>
          {childCount > 0 && (
            <span className="flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              {childCount} sub-branches
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const CreateBranchDialog = ({ branches, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    city: '',
    state: '',
    country: 'USA',
    timezone: 'America/New_York',
    parent_branch_id: '',
  });

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      toast.error('Name and code are required');
      return;
    }

    setLoading(true);
    try {
      await branchAPI.create({
        ...formData,
        parent_branch_id: formData.parent_branch_id || null,
      });
      toast.success('Branch created successfully');
      setOpen(false);
      setFormData({
        name: '',
        code: '',
        address: '',
        city: '',
        state: '',
        country: 'USA',
        timezone: 'America/New_York',
        parent_branch_id: '',
      });
      onCreated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create branch');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-branch-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Branch
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Branch</DialogTitle>
          <DialogDescription>Add a new branch location</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Branch Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Main Warehouse"
                required
                data-testid="branch-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Branch Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                placeholder="MWH-001"
                required
                data-testid="branch-code-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Parent Branch</Label>
            <Select 
              value={formData.parent_branch_id} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, parent_branch_id: v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="None (Top-level branch)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">None (Top-level branch)</SelectItem>
                {branches.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Address</Label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              placeholder="123 Main St"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>City</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                placeholder="New York"
              />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Input
                value={formData.state}
                onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                placeholder="NY"
              />
            </div>
            <div className="space-y-2">
              <Label>Country</Label>
              <Input
                value={formData.country}
                onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                placeholder="USA"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Timezone</Label>
            <Select 
              value={formData.timezone} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, timezone: v }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timezones.map(tz => (
                  <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-branch-btn">
              {loading ? 'Creating...' : 'Create Branch'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function BranchesPage() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const response = await branchAPI.getAll(true);
      setBranches(response.data.branches || []);
    } catch (error) {
      console.error('Failed to fetch branches:', error);
      toast.error('Failed to load branches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const filteredBranches = branches.filter(b => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      b.name.toLowerCase().includes(query) ||
      b.code.toLowerCase().includes(query) ||
      b.city?.toLowerCase().includes(query)
    );
  });

  const getChildCount = (branchId) => {
    return branches.filter(b => b.parent_branch_id === branchId).length;
  };

  return (
    <div className="space-y-6" data-testid="branches-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Branches</h1>
          <p className="text-slate-600">{branches.length} locations</p>
        </div>
        <CreateBranchDialog branches={branches} onCreated={fetchBranches} />
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search branches..."
          className="pl-10"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-testid="search-branches"
        />
      </div>

      {/* Branch Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-36 w-full" />
          ))}
        </div>
      ) : filteredBranches.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No branches found</h3>
            <p className="text-slate-500">
              {searchQuery ? 'Try adjusting your search' : 'Create your first branch'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBranches.map((branch) => (
            <BranchCard key={branch.id} branch={branch} childCount={getChildCount(branch.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
